import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Home from './pages/Home';
import NavBar from './components/NavBar';
import ProductCreatePage from './pages/ProductCreatePage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CategoryManagementPage from './pages/CategoryManagementPage';
import OrderPage from './pages/order/OrderPage';
import TableListPage from './pages/admin/TableListPage';
import TableCreatePage from './pages/admin/TableCreatePage';
import TableQrPrintPage from './pages/admin/TableQrPrintPage';
import ProductManagementPage from './pages/admin/ProductManagementPage';
import SalesManagementPage from './pages/admin/SalesManagementPage';
import StoreOwnerManagementPage from './pages/admin/StoreOwnerManagementPage';
import { WebSocketProvider } from './components/WebSocketContext';
import axiosInstance from './api/commonAPI';

interface AdminInfo {
    username: string;
    role: string;
    storeId: number | null;
}

const SalesManagementWrapper: React.FC = () => {
    const { storeId } = useParams<{ storeId: string }>();
    const storeIdNum = storeId ? parseInt(storeId, 10) : null;

    if (!storeIdNum) return <div>매장 ID가 없습니다.</div>;

    return <SalesManagementPage key={storeIdNum} storeId={storeIdNum} />;
};

function NavBarWrapper({ isLoggedIn, username, role, setIsLoggedIn }: any) {
    const location = useLocation();
    const hideNavBarPaths = ['/order/store'];
    const shouldShowNavBar = isLoggedIn && !hideNavBarPaths.some(path => location.pathname.startsWith(path));

    return shouldShowNavBar ? (
        <NavBar isLoggedIn={isLoggedIn} username={username} role={role} setIsLoggedIn={setIsLoggedIn} />
    ) : null;
}

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [username, setUsername] = useState<string | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [storeId, setStoreId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (window.location.pathname.startsWith('/order/store/')) {
            setIsLoggedIn(false);
            setUsername(null);
            setRole(null);
            setStoreId(null);
            setIsLoading(false);
            return;
        }

        axiosInstance.get<AdminInfo>('/api/admin/auth/me')
            .then(({ data }) => {
                setIsLoggedIn(true);
                setUsername(data.username);
                setRole(data.role);
                setStoreId(data.storeId);
            })
            .catch(() => {
                setIsLoggedIn(false);
                setUsername(null);
                setRole(null);
                setStoreId(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <WebSocketProvider>
            <Router>
                <NavBarWrapper
                    isLoggedIn={isLoggedIn}
                    username={username}
                    role={role}
                    setIsLoggedIn={setIsLoggedIn}
                />
                <Routes>
                    <Route
                        path="/login"
                        element={
                            <Login
                                onLoginSuccess={(adminInfo) => {
                                    setIsLoggedIn(true);
                                    setUsername(adminInfo.username);
                                    setRole(adminInfo.role);
                                    setStoreId(adminInfo.storeId);
                                }}
                            />
                        }
                    />
                    <Route path="/order/store/:storeId/table/:tableId" element={<OrderPage />} />
                    <Route path="/register" element={<Register />} />
                    <Route
                        path="/"
                        element={
                            !isLoggedIn ? (
                                <Navigate to="/login" replace />
                            ) : role === 'ROLE_SERVER_ADMIN' ? (
                                <Navigate to="/admin/store-owners" replace />
                            ) : (
                                <Home storeId={storeId} />
                            )
                        }
                    />

                    <Route
                        element={
                            <ProtectedRoute
                                isLoggedIn={isLoggedIn}
                                role={role}
                                requiredRole="ROLE_SERVER_ADMIN"
                            />
                        }
                    >
                        <Route path="/admin/store-owners" element={<StoreOwnerManagementPage />} />
                    </Route>

                    <Route
                        element={
                            <ProtectedRoute
                                isLoggedIn={isLoggedIn}
                                role={role}
                                requiredRole="ROLE_STORE_OWNER"
                            />
                        }
                    >
                        <Route path="/admin/stores/:storeId/tables" element={<TableListPage />} />
                        <Route path="/admin/stores/:storeId/tables/new" element={<TableCreatePage />} />
                        <Route path="/admin/stores/:storeId/tables/qr-print" element={<TableQrPrintPage />} />
                        <Route path="/admin/stores/:storeId/products/new" element={<ProductCreatePage />} />
                        <Route path="/admin/stores/:storeId/categories" element={<CategoryManagementPage />} />
                        <Route path="admin/stores/:storeId/products" element={<ProductManagementPage />} />
                        <Route path="/admin/stores/:storeId/sales" element={<SalesManagementWrapper />} />
                    </Route>

                    <Route
                        path="*"
                        element={<Navigate to={isLoggedIn ? "/" : "/login"} replace />}
                    />
                </Routes>
            </Router>
        </WebSocketProvider>
    );
}

export default App;
